import { CSSProperties, ReactNode } from 'react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import cx from 'classnames'

import { ArrowDropdownDown } from 'common/icons'

import Button, { ButtonProps } from './button'

export interface ButtonOptionsProps {
  key: string
  label?: string
  onClick?: () => void
  type?: 'item' | 'divider'
  isDisabled?: boolean
}

interface ButtonWithDropdownProps extends ButtonProps {
  overlayClassName?: string
  options: ButtonOptionsProps[] | undefined
  menuStyle?: CSSProperties
  menuItemStyle?: CSSProperties
  animation?: string
  placement?: string
  suffixIcon?: ReactNode | false
}

export const ButtonWithDropdown = ({
  options,
  className,
  children,
  variant,
  overlayClassName,
  menuStyle,
  menuItemStyle,
  suffixIcon = <ArrowDropdownDown isDefaultColor={false} />,
  animation: _animation,
  placement = 'bottomRight',
  ...rest
}: ButtonWithDropdownProps) => {
  const side = 'bottom'
  const align = placement === 'bottomLeft' ? 'start' : 'end'

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button
          className={cx('updated-component', className)}
          variant={variant}
          suffixIcon={suffixIcon}
          {...rest}
        >
          {children}
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          side={side}
          align={align}
          sideOffset={2}
          className={cx(
            'bg-white border border-coolGray-200 rounded shadow-lg min-w-[160px] w-max z-[1070]',
            'data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out',
            overlayClassName
          )}
          style={menuStyle}
        >
          {options?.map((option) => {
            const { key, isDisabled, label, onClick, type = 'item' } = option
            return type === 'item' ? (
              <DropdownMenu.Item
                key={key}
                disabled={isDisabled}
                onSelect={onClick}
                style={menuItemStyle}
                className={cx(
                  'h-8 pl-4 pr-4 flex items-center text-coolGray-800 outline-none',
                  {
                    'cursor-pointer hover:bg-coolGray-50 focus:bg-coolGray-50':
                      !isDisabled,
                    'cursor-not-allowed opacity-50': isDisabled,
                  }
                )}
              >
                {label}
              </DropdownMenu.Item>
            ) : (
              <DropdownMenu.Separator
                key={key}
                className="my-1 border-t border-coolGray-200"
              />
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export default ButtonWithDropdown
